import { useContext } from 'react';
import useAuth from './useAuth';
import useActiveCondominio from './useActiveCondominio';
import FeaturesContext from '../context/FeaturesContext';
import axios from 'axios';

/**
 * Hook para acessar features e utilitários
 * 
 * SEM requisições HTTP - Consome dados do FeaturesContext
 * Oferece métodos auxiliares para verificar acesso e features
 * 
 * @returns {Object} { features, loading, error, isEnabled, hasAccessToFeature, hasCategoryAccess, refetch, updateFeature }
 */
export const useFeatures = () => {
  const { user } = useAuth();
  const { activeCondominio } = useActiveCondominio();
  const { features, loading, error, refetch } = useContext(FeaturesContext);

  /**
   * Verifica se uma feature está ativa
   * @param {string} featureName - Nome da feature (ex: 'alertas', 'exaustores')
   * @returns {boolean} Se feature está ativa
   */
  const isEnabled = (featureName) => {
    const feature = features.find(f => f.nome === featureName);
    return feature?.ativo === 1;
  };

  /**
   * Verifica se usuário tem acesso a uma feature específica (baseado no cargo)
   * @param {string} featureName - Nome da feature
   * @returns {boolean} Se feature está ativa E usuário tem permissão
   */
  const hasAccessToFeature = (featureName) => {
    if (!user) return false;
    
    // Admin tem acesso a tudo
    if (user.cargo === 'admin') return true;

    const feature = features.find(f => f.nome === featureName);
    if (!feature || feature.ativo !== 1) return false;

    // Verifica se cargo está na lista de visibilidade
    const visibleCargos = Array.isArray(feature.visivel_para_cargo)
      ? feature.visivel_para_cargo
      : JSON.parse(feature.visivel_para_cargo || '[]');

    return visibleCargos.includes(user.cargo);
  };

  /**
   * Verifica se usuário tem acesso a alguma feature de uma categoria específica
   * @param {string} categoria - Categoria da feature (ex: 'configuracao')
   * @returns {boolean}
   */
  const hasCategoryAccess = (categoria) => {
    if (!user) return false;

    // Admin sempre tem acesso
    if (user.cargo === 'admin') return true;

    // Verificar se tem acesso a qualquer feature da categoria
    return features.some(feature => 
      feature.categoria === categoria && 
      hasAccessToFeature(feature.nome)
    );
  };

  /**
   * Atualiza uma feature (apenas admin)
   * @param {string} featureName - Nome da feature
   * @param {Object} config - { ativo: true, visivel_para_cargo: ["admin", "sindico"] }
   * 
   * ⚠️ NÃO faz refetch automaticamente - chamar refetch() manualmente após todas atualizações
   */
  const updateFeature = async (featureName, config) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      await axios.put(
        `${apiUrl}/api/features/${featureName}`,
        config,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-condominio-id': activeCondominio
          }
        }
      );

      // ✅ NÃO fazer refetch aqui - evita N refetches ao atualizar múltiplas features
      // Componente deve chamar refetch() UMA VEZ após todas as atualizações
    } catch (err) {
      console.error('Erro ao atualizar feature:', err);
      throw err;
    }
  };

  return {
    features,
    loading,
    error,
    isEnabled,
    hasAccessToFeature,
    hasCategoryAccess,
    refetch,
    updateFeature
  };
};

export default useFeatures;
